import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Grid,
  CircularProgress,
  Chip,
  Divider,
  useMediaQuery,
  useTheme,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  TextField
} from '@mui/material';
import {
  ArrowBack,
  PowerSettingsNew,
  AddCircle,
  Wifi,
  SystemUpdateAlt,
  Schedule,
  PowerOff,
  Power, Visibility, VisibilityOff
} from '@mui/icons-material';
import deviceApi from '../services/device-api';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';

function GatewayDetail({ gateway, onBack }) {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWifiPass, setShowWifiPass] = useState(false);
  const [sendingCommand, setSendingCommand] = useState(null);
  const [aliveEvents, setAliveEvents] = useState([]);
  const [chartStart, setChartStart] = useState(() =>
    new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString().slice(0, 16)
  );
  const [chartEnd, setChartEnd] = useState(() =>
    new Date().toISOString().slice(0, 16)
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Fetch nodes function dùng chung (phải nằm trước sendCommand vì sendCommand có thể gọi lại)
  const fetchNodes = async () => {
    setLoading(true);
    try {
      const res = await deviceApi.get(`/api/gateways/${gateway.id}/nodes`);
      setNodes(res.data || []);
    } catch (error) {
      console.error('Failed to fetch nodes', error);
      setNodes([]);
    } finally {
      setLoading(false);
    }
  };

  // Utility delay
  const delay = (ms) => new Promise((res) => setTimeout(res, ms));
  
  // Gọi fetchNodes khi gateway thay đổi (lần đầu mở giao diện)
  useEffect(() => {
    if (gateway && gateway.id) {
      fetchNodes();
    } else {
      // nếu không có gateway id thì không show loading vô hạn
      setLoading(false);
      setNodes([]);
    }
  }, [gateway?.id]);

  const isRelayOn = (value) => String(value).toUpperCase() === 'ON';

  const sendCommand = async (cmd) => {
    let payload = {};
    // Xử lý lệnh đặc biệt
    if (cmd.action === 'reboot') {
      payload = {
        command: 'reboot',
        cid: Math.floor(Math.random() * 9) + 1
      };
    } else if (cmd.action === 'deletenode') {
      if (cmd.index === null || cmd.index === undefined || !Number.isInteger(cmd.index)) {
          alert("Node ID is required and must be an integer to delete a node");
          return;
      }

      payload = {
          command: 'deletenode',
          index: cmd.index,
          cid: Math.floor(Math.random() * 9) + 1
      };
    } else if (cmd.action === 'addnode') {
      payload = {
        command: 'addnewnode',
        cid: Math.floor(Math.random() * 9) + 1
      };
    } else if (cmd.action === 'configwifi') {
      payload = { command: 'configwifi', cid: Math.floor(Math.random() * 9) + 1 };
    }else if (cmd.action === 'updatefw') {
      payload = { command: 'updatefw', cid: Math.floor(Math.random() * 9) + 1 };
    }else if (cmd.action === 'setinterval') {
      payload = { command: 'confignode', configdata: { nodeidx: cmd.index, length: 2, data: [1, cmd.hour] }, cid: Math.floor(Math.random() * 9) + 1 };
    } else if (cmd.action === 'control') {
      const relay = cmd.relay || null;
      const on = !!cmd.on;
      const node = nodes.find((item) => item.index === cmd.node_idx);

      if (!relay || !node) {
        alert('Không tìm thấy node hoặc relay để gửi lệnh điều khiển');
        return;
      }

      if (
        (relay === 1 && (node.relay2 === null || node.relay2 === undefined)) ||
        (relay === 2 && (node.relay1 === null || node.relay1 === undefined))
      ) {
        alert('Không đủ trạng thái relay hiện tại để gửi CMD 0x03 an toàn');
        return;
      }

      const relay1State = relay === 1 ? (on ? 1 : 0) : (isRelayOn(node.relay1) ? 1 : 0);
      const relay2State = relay === 2 ? (on ? 1 : 0) : (isRelayOn(node.relay2) ? 1 : 0);

      // Gateway tự thêm targetNodeAddr từ nodeidx, nên data bắt đầu từ byte 8 của CMD 0x03.
      payload = {
        command: 'confignode',
        configdata: {
          nodeidx: cmd.node_idx,
          length: 9,
          data: [0x03, relay1State, 0x00, 0x00, relay2State, 0x00, 0x00, 0x00, 0x00]
        },
        cid: Math.floor(Math.random() * 9) + 1
      };
    } else {
      payload = { ...cmd, cid: Math.floor(Math.random() * 9) + 1 };
    }
    // Gắn relay vào tên trạng thái nếu có, để disable nút tương ứng khi đang gửi
    setSendingCommand(
      cmd.action +
      (cmd.relay ? `-relay${cmd.relay}` : '') +
      (cmd.on === true ? '-on' : cmd.on === false ? '-off' : '')
    );
    try {
      const res = await deviceApi.post(`/api/gateways/${gateway.id}/command`, payload);
      const data = res.data;
      console.log("Command response:", data);
      if (data.status === "timeout") {
        alert(data.resmsg || "Device did not respond or returned error");
      } else if (data.status === "success") {
        if (data.data && data.data.resmsg && data.data.resmsg === "Success") {
          // Nếu là lệnh control thì cập nhật trạng thái relay trên UI ngay (optimistic)
          if (cmd.action === 'control') {
            setNodes(prev =>
              prev.map(n => {
                if (n.index !== cmd.node_idx) return n;
                const updated = { ...n };
                if (cmd.relay === 1) updated.relay1 = cmd.on ? 'ON' : 'OFF';
                else if (cmd.relay === 2) updated.relay2 = cmd.on ? 'ON' : 'OFF';
                return updated;
              })
            );
          }
          // Thông báo cho user, sau khi bấm OK tiếp tục fetch lại nodes để đồng bộ chính xác từ backend
          alert("Command sent successfully");
          if (cmd.action === 'control') {
            await delay(4000);
            await fetchNodes();
          }
        } else if (data.data && data.data.resmsg) {
          alert(data.data.resmsg);
          if (cmd.action === 'control') {
            await delay(4000);
            await fetchNodes();
          }
        } else {
          alert("Failed to send command");
        }
      } else {
        alert(data);
      }
    } catch (error) {
      alert("Failed to send command");
    } finally {
      setSendingCommand(null);
    }
  };
  const getWifiRssiColor = (rssi) => {
    if (rssi === null || rssi === undefined) return 'inherit';
    if (rssi >= -60) return '#4caf50';      // Tốt
    if (rssi >= -75) return '#ff9800';      // Trung bình
    return '#f44336';                       // Yếu
  };
  // Hàm đánh giá màu RSSI LoRa
  const getRssiColor = (rssi) => {
    if (rssi === null || rssi === undefined) return 'inherit';
    if (rssi >= -115) return '#4caf50';      // Tốt
    if (rssi >= -126) return '#ff9800';     // Trung bình
    return '#f44336';                       // Yếu
  };

  // Hàm đánh giá màu SNR LoRa
  const getSnrColor = (snr) => {
    if (snr === null || snr === undefined) return 'inherit';
    if (snr >= -7) return '#4caf50';         // Tốt
    if (snr >= -15) return '#ff9800';         // Trung bình
    return '#f44336';                       // Yếu
  };

  const handleDeleteNode = async (index) => {
    if (!window.confirm(`Delete node ${index}?`)) return;
    try {
      await sendCommand({ action: 'deletenode', index });
      setNodes(nodes.filter(n => n.index !== index));
    } catch (e) {
      alert('Delete failed');
    }
  };
  const [openSchedule, setOpenSchedule] = useState(false);
  const [scheduleNode, setScheduleNode] = useState(null);
  const [scheduleHour, setScheduleHour] = useState(1);
  const handleOpenSchedule = (node) => {
    setScheduleNode(node);
    setScheduleHour(1);
    setOpenSchedule(true);
  };
  const handleScheduleOk = async () => {
    if (!scheduleNode) return;
    await sendCommand({
      action: 'setinterval',
      hour: scheduleHour,
      index: scheduleNode.index
    });
    setOpenSchedule(false);
  };

  useEffect(() => {
    const fetchAliveEvents = async () => {
      try {
        const res = await deviceApi.get('/api/events', {
          params: {
            gw_id: gateway.id,
            msgtype: 'alive',
            sort: 'desc',
            startDate: chartStart.replace('T', ' ') + ':00',
            endDate: chartEnd.replace('T', ' ') + ':00'
          }
        });
        setAliveEvents(res.data.events || []);
      } catch (error) {
        setAliveEvents([]);
      }
    };
    if (gateway.id) fetchAliveEvents();
  }, [gateway.id, chartStart, chartEnd]);

  // Hàm so sánh version dạng "x.y.z"
  function isVersionGreater(v1, v2) {
    const a = v1.split('.').map(Number);
    const b = v2.split('.').map(Number);
    for (let i = 0; i < Math.max(a.length, b.length); i++) {
      if ((a[i] || 0) > (b[i] || 0)) return true;
      if ((a[i] || 0) < (b[i] || 0)) return false;
    }
    return false;
  }

  // Xóa hoặc comment đoạn này:
  // useEffect(() => {
  //   if (aliveEvents.length > 0) {
  //     // const end = new Date();
  //     const end = chartEnd ? new Date(chartEnd) : new Date();
  //     end.setMinutes(0, 0, 0);
  //     setChartEnd(end.toISOString().slice(0, 16));
  //     if (!chartStart) {
  //       // Nếu không có chartStart, đặt nó là 48 giờ trước thời điểm kết thúc
  //       setChartStart(new Date(end.getTime() - 48 * 60 * 60 * 1000).toISOString().slice(0, 16));
  //     }
  //   }
  // }, [aliveEvents]);

  // Xử lý dữ liệu cho biểu đồ
  const getHourlyStatus = () => {
    const start = new Date(chartStart);
    const end = new Date(chartEnd);
    const hours = [];
    const status = [];
    // Tạo set các giờ có alive (dùng UTC)
    const aliveSet = new Set(
      aliveEvents
        .map(e => {
          // Chuyển về UTC
          const d = new Date((e.received_at || e.timestamp).replace(' ', 'T') + 'Z');
          d.setMinutes(0, 0, 0);
          return d.getTime();
        })
    );
    for (let d = new Date(start); d <= end; d.setHours(d.getHours() + 1)) {
      // Đảm bảo d là UTC
      const utc = new Date(d.getTime());
      utc.setMinutes(0, 0, 0);
      const label = utc.toLocaleString('en-US', { hour12: false, year: '2-digit', month: '2-digit', day: '2-digit', hour: '2-digit' });
      hours.push(label);
      status.push(aliveSet.has(utc.getTime()) ? 1 : 0);
    }
    return { hours, status };
  };

  const { hours, status } = getHourlyStatus();

  return (
    <Box component={Paper} elevation={2} p={2} sx={{ borderRadius: 2 }}>
      <Box display="flex" alignItems="center" mb={2}>
        <IconButton onClick={onBack} sx={{ mr: 1 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" component="h1">
          Gateway Details
        </Typography>
      </Box>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="subtitle2" color="textSecondary">Gateway ID</Typography>
          <Typography variant="body1">{gateway.id}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="subtitle2" color="textSecondary">IP Address</Typography>
          <Typography variant="body1">{gateway.ip || 'N/A'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="subtitle2" color="textSecondary">Firmware</Typography>
          <Typography variant="body1">{gateway.fw || 'N/A'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="subtitle2" color="textSecondary">Hardware</Typography>
          <Typography variant="body1">{gateway.hw || 'N/A'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="subtitle2" color="textSecondary">Last Event</Typography>
          <Typography variant="body1">
            {gateway.last_event ? new Date(gateway.last_event).toLocaleString() : 'N/A'}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="subtitle2" color="textSecondary">WiFi SSID</Typography>
          <Typography variant="body1">{gateway.ssid || 'N/A'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="subtitle2" color="textSecondary">WiFi Password</Typography>
          <Box display="flex" alignItems="center">
            <Typography variant="body1" sx={{ mr: 1 }}>
              {showWifiPass ? (gateway.pass || 'N/A') : (gateway.pass ? '••••••••' : 'N/A')}
            </Typography>
            {gateway.pass && (
              <IconButton
                size="small"
                onClick={() => setShowWifiPass((v) => !v)}
                aria-label={showWifiPass ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
              >
                {showWifiPass ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="subtitle2" color="textSecondary">WiFi RSSI</Typography>
          <Typography variant="body1">
            <span style={{ color: getWifiRssiColor(gateway.rssi), fontWeight: 600 }}>
              {gateway.rssi !== null && gateway.rssi !== undefined ? `${gateway.rssi} dBm` : 'N/A'}
            </span>
          </Typography>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Box mb={3}>
        <Typography variant="subtitle1" gutterBottom>Gateway Actions</Typography>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {gateway.fw && gateway.fw !== 'N/A' && (
            <>
              <Tooltip title="Reboot gateway">
                <Button
                  variant="outlined"
                  startIcon={<PowerSettingsNew />}
                  onClick={() => sendCommand({ action: 'reboot' })}
                  disabled={sendingCommand === 'reboot'}
                >
                  {sendingCommand === 'reboot' ? <CircularProgress size={24} /> : 'Reboot'}
                </Button>
              </Tooltip>
              <Tooltip title="Add new node">
                <Button
                  variant="outlined"
                  startIcon={<AddCircle />}
                  onClick={() => sendCommand({ action: 'addnode' })}
                  disabled={sendingCommand === 'addnode'}
                >
                  {sendingCommand === 'addnode' ? <CircularProgress size={24} /> : 'Add Node'}
                </Button>
              </Tooltip>
              {/* Nút Update Firmware */}
              <Tooltip title="Update Firmware">
                <Button
                  variant="outlined"
                  startIcon={<SystemUpdateAlt />}
                  onClick={() => {
                    if (window.confirm('Bạn có chắc chắn muốn cập nhật firmware không?')) {
                      sendCommand({ action: 'updatefw' });
                    }
                  }}
                  disabled={sendingCommand === 'updatefw'}
                >
                  Cập nhật Firmware
                </Button>

              </Tooltip>
              {/* <Tooltip title="Configure WiFi">
                <Button
                  variant="outlined"
                  startIcon={<Wifi />}
                  onClick={() => sendCommand({ action: 'configwifi' })}
                  disabled={sendingCommand === 'configwifi'}
                >
                  {sendingCommand === 'configwifi' ? <CircularProgress size={24} /> : 'Config WiFi'}
                </Button>
              </Tooltip> */}
            </>
          )}
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box>
        <Typography variant="subtitle1" gutterBottom>Connected Nodes</Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : nodes.length === 0 ? (
          <Typography variant="body2" color="textSecondary" textAlign="center" p={2}>
            No nodes connected to this gateway
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell>Node ID</TableCell>
                  <TableCell>Index</TableCell>
                  <TableCell>Battery</TableCell>
                  <TableCell>Last Event</TableCell>
                  <TableCell>RSSI</TableCell>
                  <TableCell>SNR</TableCell>
                  <TableCell>FW</TableCell>
                  <TableCell>Repeater</TableCell>
                  <TableCell>SleepTime</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {nodes.map((node) => (
                  <TableRow key={node.node_id}>
                    <TableCell>
                      <Chip label={node.node_id} size="small" />
                    </TableCell>
                    <TableCell>{node.index}</TableCell>
                    <TableCell>
                      {node.battery !== null && node.battery !== undefined ? node.battery : 'N/A'}
                    </TableCell>
                    {/* <TableCell>
                      {node.last_event ? new Date(node.last_event).toLocaleString() : 'N/A'}
                    </TableCell> */}
                    <TableCell>
                      {node.last_event
                        ? (() => {
                          // Parse chuỗi "YYYY-MM-DD HH:mm:ss.SSS" thành Date (UTC)
                          const utcDate = new Date(node.last_event.replace(' ', 'T') + 'Z');
                          // Cộng thêm 7 giờ (7 * 60 * 60 * 1000 ms)
                          const vnDate = new Date(utcDate.getTime());
                          return vnDate.toLocaleString('en-US', { hour12: false });
                        })()
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <span style={{ color: getRssiColor(node.rssi), fontWeight: 600 }}>
                        {node.rssi !== null && node.rssi !== undefined ? `${node.rssi} dBm` : 'N/A'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span style={{ color: getSnrColor(node.snr), fontWeight: 600 }}>
                        {node.snr !== null && node.snr !== undefined ? node.snr : 'N/A'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {node.box_fw !== null && node.box_fw !== undefined ? (
                        <span style={{ color: '#4caf50', fontWeight: 600 }}>
                          {node.box_fw}
                        </span>
                      ) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {node.repeater !== null && node.repeater !== undefined ? (
                        <span style={{ color: node.repeater > 0 ? '#4caf50' : '#f44336', fontWeight: 600 }}>
                          {node.repeater > 0 ? 'Yes' : 'No'}
                        </span>
                      ) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {node.sleeptime !== null && node.sleeptime !== undefined ? (
                        <span style={{ color: '#2196f3', fontWeight: 600 }}>
                          {node.sleeptime} hours
                        </span>
                      ) : 'N/A'}
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={1} justifyContent={isMobile ? 'center' : 'flex-end'}>
                        {isVersionGreater(gateway.fw || "0.0.0", "4.0.1") && (
                          <>
                            <Tooltip title="Schedule">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenSchedule(node)}
                              >
                                <Schedule fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {/* Relay 1 */}
                            {node.relay1 !== null && node.relay1 !== undefined && (
                              <Tooltip title={`Relay1: ${node.relay1}`}>
                                <IconButton
                                  size="small"
                                  color={node.relay1 === 'ON' ? 'success' : 'warning'}
                                  onClick={() => sendCommand({ action: 'control', node_idx: node.index, relay: 1, on: node.relay1 !== 'ON' })}
                                  disabled={sendingCommand && sendingCommand.startsWith('control-relay1')}
                                >
                                  {node.relay1 === 'ON' ? <Power fontSize="small" /> : <PowerOff fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}
                            {/* Relay 2 */}
                            {node.relay2 !== null && node.relay2 !== undefined && (
                              <Tooltip title={`Relay2: ${node.relay2}`}>
                                <IconButton
                                  size="small"
                                  color={node.relay2 === 'ON' ? 'success' : 'warning'}
                                  onClick={() => sendCommand({ action: 'control', node_idx: node.index, relay: 2, on: node.relay2 !== 'ON' })}
                                  disabled={sendingCommand && sendingCommand.startsWith('control-relay2')}
                                >
                                  {node.relay2 === 'ON' ? <Power fontSize="small" /> : <PowerOff fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                        <Tooltip title="Delete Node">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteNode(node.index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
      <Dialog open={openSchedule} onClose={() => setOpenSchedule(false)}>
        <DialogTitle>Đặt lịch cho node {scheduleNode?.node_id}</DialogTitle>
        <DialogContent>
          <Box width={250} mt={2}>
            <Typography gutterBottom>Chọn thời gian (giờ): {scheduleHour}</Typography>
            <Slider
              value={scheduleHour}
              min={1}
              max={24}
              step={1}
              marks
              valueLabelDisplay="auto"
              onChange={(_, v) => setScheduleHour(v)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSchedule(false)}>Hủy</Button>
          <Button onClick={handleScheduleOk} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>

      <Box mb={3}>
        <Typography variant="subtitle1" gutterBottom>
          Alive Events Timeline
        </Typography>
        <Box display="flex" gap={2} mb={2} alignItems="center">
          <TextField
            label="Từ giờ"
            type="datetime-local"
            size="small"
            value={chartStart}
            onChange={e => setChartStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Đến giờ"
            type="datetime-local"
            size="small"
            value={chartEnd}
            onChange={e => setChartEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        {hours.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No data in selected range.
          </Typography>
        ) : (
          <Line
            data={{
              labels: hours,
              datasets: [
                {
                  label: 'Online/Offline',
                  data: status,
                  pointRadius: 6,
                  pointBackgroundColor: status.map(v => v ? '#4caf50' : '#bdbdbd'),
                  borderColor: 'rgba(0,0,0,0.1)',
                  fill: false,
                  showLine: false,
                }
              ]
            }}
            options={{
              scales: {
                y: {
                  display: false,
                  min: 0,
                  max: 1
                }
              },
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: ctx => ctx.raw ? 'Online' : 'Offline'
                  }
                }
              }
            }}
            height={80}
          />
        )}
      </Box>
    </Box>
  );
}

export default GatewayDetail;
